import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { environment } from '../../../../environments/environment';

/**
 * Generic Email launcher — a mail icon that opens a menu with "New Email" plus this
 * module's email templates (searchable + scrollable). Picking New Email opens the
 * compose dialog blank; picking a template opens it with that template inserted.
 *
 * Drop it into any module's action bar:
 *   <app-email-launcher
 *     [moduleId]="16" [moduleName]="'Bids'"
 *     [recordId]="bid.id" [recordName]="bid.bid_number"
 *     [defaultSubject]="bid.bid_number + ' - ' + bid.name">
 *   </app-email-launcher>
 *
 * The sent email auto-links to the record (module_id + module_data_id → email_module_data)
 * so it shows in that record's EMAILS tab — handled by ComposeEmailComponent's send flow.
 */
@Component({
  selector: 'app-email-launcher',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatIconModule, MatMenuModule, MatTooltipModule],
  templateUrl: './email-launcher.component.html',
  styleUrls: ['./email-launcher.component.scss'],
  // None so the menu panel (rendered in a CDK overlay outside this component) picks up
  // the styles below. Every selector is namespaced under .email-launcher-* to stay safe.
  encapsulation: ViewEncapsulation.None,
})
export class EmailLauncherComponent {
  /** The ERP module id (e.g. 16 = Bid). Scopes the templates and links the sent email. */
  @Input() moduleId!: number;
  /** Human module name stored on the link (e.g. 'Bids'). */
  @Input() moduleName = '';
  /** The record id this email is about (e.g. the bid id) — used for the module link. */
  @Input() recordId?: number;
  /** The record's display reference (e.g. bid number) — stored as the link reference. */
  @Input() recordName = '';
  /** Subject to pre-fill (e.g. "412-OPP - Domnic Lounge"). */
  @Input() defaultSubject = '';
  /** Trigger tooltip. */
  @Input() tooltip = 'Email';

  @ViewChild(MatMenuTrigger) private menuTrigger?: MatMenuTrigger;

  templates: any[] = [];
  filtered: any[] = [];
  searchTerm = '';
  loading = false;
  private loaded = false;

  private readonly apiUrl = `${environment.apiBaseUrl}/api/v1/`;

  constructor(private readonly http: HttpClient, private readonly dialog: MatDialog) {}

  /** Load templates the first time the menu opens. */
  onMenuOpened(): void {
    if (!this.loaded) {
      this.loadTemplates();
    }
  }

  onMenuClosed(): void {
    this.searchTerm = '';
    this.filter();
  }

  loadTemplates(): void {
    this.loading = true;
    let url = `${this.apiUrl}email-templates/compose-picker`;
    if (this.moduleId) {
      url += `?module_id=${this.moduleId}`;
    }
    this.http.get<any>(url).subscribe({
      next: (res) => {
        this.templates = res?.success ? (res.data || []) : [];
        this.filter();
        this.loading = false;
        this.loaded = true;
      },
      error: () => {
        this.templates = [];
        this.filtered = [];
        this.loading = false;
        this.loaded = true;
      },
    });
  }

  filter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filtered = !term
      ? [...this.templates]
      : this.templates.filter(
          (t) =>
            t.template_name?.toLowerCase().includes(term) ||
            t.description?.toLowerCase().includes(term)
        );
  }

  newEmail(): void {
    this.menuTrigger?.closeMenu();
    this.openCompose(null);
  }

  useTemplate(template: any): void {
    this.menuTrigger?.closeMenu();

    // Without a record we can't resolve variables — insert the template as-is.
    if (!this.recordId || !this.moduleId) {
      this.openCompose(template);
      return;
    }

    // Resolve the template's {{variables}} against THIS record's real data, then compose.
    // Falls back to the raw template if the call fails, so it always opens.
    this.http
      .post<any>(`${this.apiUrl}email-templates/process-variables`, {
        template_id: template.id,
        module_id: this.moduleId,
        record_id: this.recordId,
      })
      .subscribe({
        next: (res) => {
          const processed =
            res?.success && res?.data
              ? { ...template, html_content: res.data.html, css_content: res.data.css }
              : template;
          this.openCompose(processed);
        },
        error: () => this.openCompose(template),
      });
  }

  /**
   * Open the shared compose dialog, blank or with a preset template. The compose
   * component is imported lazily so its heavy dependencies (Syncfusion RTE) load only
   * when the user actually composes — not into every page that shows this launcher.
   */
  private async openCompose(template: any | null): Promise<void> {
    const { ComposeEmailComponent } = await import('../compose-email/compose-email.component');
    this.dialog.open(ComposeEmailComponent, {
      // Centered modal (matches the ticket detail's "Send Email"), not the email
      // module's right-hand drawer.
      width: '700px',
      height: '600px',
      panelClass: 'compose-email-dialog-container',
      data: {
        mode: 'compose',
        moduleId: this.moduleId,
        moduleDataId: this.recordId,
        moduleName: this.moduleName,
        referenceName: this.recordName,
        lockModuleLink: true, // opened from the record → keep it linked (no remove X)
        prefillSubject: this.defaultSubject,
        presetTemplate: template, // null for New Email
      },
    });
  }
}
